import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { commissionsSettingsApi } from "../../../../modules/commissions/settings/api";
import {
  AssignCommissionsSettingsLevel,
  CommissionsSettings,
  CreateCommissionsSettingsRule,
  CreateUpdateCommissionsSettingsRankInterval,
  UpdateCommissionsSettingsInitialPoints,
  UpdateCommissionsSettingsRule,
  UpdateCommissionsSettingsStatus,
} from "../../../../modules/commissions/settings/types";
import type { UUID } from "../../../../typings/global";
import {
  CreateUpdateEntries,
  EntityObject,
  OptimisticLockVersion,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../types";
import { COMMISSIONS_SETTINGS_QUERY_KEYS } from "./queryKeys";

export const useGetCommissionsSettings = (id?: UUID, enabled?: boolean) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(id!),
    queryFn: async () => {
      const { data } = await commissionsSettingsApi.getCommissionsSettings({ id: id! });

      return data;
    },
    enabled,
  });
};

export const useGetProfileCommissionsSettings = (id?: UUID) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(id!),
    queryFn: async () => {
      const { data } = await commissionsSettingsApi.getProfileCommissionsSettings({ id: id! });

      return data;
    },
    enabled: !!id,
  });
};

export const useAssignCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.assignCommissionsSettingsLevel(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<AssignCommissionsSettingsLevel>) => {
      const { data } = await commissionsSettingsApi.assignCommissionsSettingsLevel(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useUnassignCommissionsSettingsLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.unassignCommissionsSettingsLevel(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.unassignCommissionsSettingsLevel(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useGetAgentCommissionsSettingsRankIntervals = (id: UUID) => {
  return useQuery({
    queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getAgentCommissionsSettingsRankIntervals(id),
    queryFn: async () => {
      const { data } = await commissionsSettingsApi.getAgentCommissionsSettingsRankIntervals({ id });

      return data;
    },
  });
};

export const useUpdateAgentCommissionsSettingsRankIntervals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.updateAgentCommissionsSettingsRankIntervals(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateEntries<CreateUpdateCommissionsSettingsRankInterval>>) => {
      const { data } = await commissionsSettingsApi.updateAgentCommissionsSettingsRankIntervals(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: COMMISSIONS_SETTINGS_QUERY_KEYS.getAgentCommissionsSettingsRankIntervals(data.agent.id),
      });
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useTogglePrimaryPaymentInclusionCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePrimaryPaymentInclusionCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePrimaryPaymentInclusionCommissionsSettings(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useToggleSecondaryPaymentInclusionCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.toggleSecondaryPaymentInclusionCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.toggleSecondaryPaymentInclusionCommissionsSettings(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useToggleUsageAsHeadCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.toggleUsageAsHeadCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.toggleUsageAsHeadCommissionsSettings(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useTogglePointsUnificationCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePointsUnificationCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePointsUnificationCommissionsSettings(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useTogglePrimaryManagerialCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePrimaryManagerialCommissions(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePrimaryManagerialCommissions(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useToggleSecondaryManagerialCommissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.toggleSecondaryManagerialCommissions(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.toggleSecondaryManagerialCommissions(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useTogglePromotionAllowanceCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.togglePromotionAllowanceCommissionsSettings(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await commissionsSettingsApi.togglePromotionAllowance(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useUpdateInitialPointsCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.updateInitialPointsCommissionsSettings(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateCommissionsSettingsInitialPoints>) => {
      const { data } = await commissionsSettingsApi.updateInitialPoints(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useUpdateStatusCommissionsSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.updateStatusCommissionsSettings(),
    mutationFn: async (payload: EntityObject<UpdateCommissionsSettingsStatus>) => {
      const { data } = await commissionsSettingsApi.updateStatusCommissionsSettings(payload);

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(data.agent.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useCreateCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.createCommissionsSettingsRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateCommissionsSettingsRule>) => {
      const { data } = await commissionsSettingsApi.createCommissionsSettingsRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, rules: [...oldData.rules, data] }),
      );
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.updateCommissionsSettingsRule(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateCommissionsSettingsRule>) => {
      const { data } = await commissionsSettingsApi.updateCommissionsSettingsRule(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          rules: [...oldData.rules.filter((rule) => rule.id !== mutatedPayload.id2), data],
        }),
      );
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCommissionsSettingsRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.deleteCommissionsSettingsRule(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsApi.deleteCommissionsSettingsRule(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          rules: oldData.rules.filter((rule) => rule.id !== mutatedPayload.id2),
        }),
      );
      Notification.itemDeletedNotification();
    },
  });
};

export const useUploadCommissionsSettingsAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.uploadCommissionsSettingsAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await commissionsSettingsApi.uploadCommissionsSettingsAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, attachments: data }),
      );

      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(mutatedPayload.id),
        (oldData: CommissionsSettings) => ({ ...oldData, attachments: data }),
      );
    },
  });
};

export const useDeleteCommissionsSettingsAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: COMMISSIONS_SETTINGS_QUERY_KEYS.deleteCommissionsSettingsAttachments(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      return commissionsSettingsApi.deleteCommissionsSettingsAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          attachments: oldData.attachments.filter((item) => item.id !== mutatedPayload.id2),
        }),
      );

      queryClient.setQueryData(
        COMMISSIONS_SETTINGS_QUERY_KEYS.getProfileCommissionsSettings(mutatedPayload.id1),
        (oldData: CommissionsSettings) => ({
          ...oldData,
          attachments: oldData.attachments.filter((item) => item.id !== mutatedPayload.id2),
        }),
      );
    },
  });
};
